/**
 * Audio Analysis Test Page
 *
 * Admin-only page for testing sales audio analysis.
 * Allows recording up to 30 seconds of audio and receiving:
 * - Transcription
 * - LLM-based sales quality feedback
 * - Optional prosody metrics (via Python service toggle)
 */

import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { analyzeAudio, SpeechAnalysisResult, LLMScoreItem } from "../../../api/speechAnalysis";
import TitleSubtitle from "../../../components/common/TitleSubtitle";
import HorizontalSeparator from "../../../components/common/HorizontalSeparator";
import Button from "../../../components/ui/Button";
import styles from "./AudioAnalysisTest.module.css";

const MAX_RECORDING_SECONDS = 30;
const ACCEPTED_AUDIO_TYPES = ["audio/mp3", "audio/mpeg", "audio/wav", "audio/webm", "audio/m4a", "audio/x-m4a"];
const MAX_FILE_SIZE_MB = 10;

const AudioAnalysisTest: React.FC = () => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;

    // Input mode: 'record' or 'upload'
    const [inputMode, setInputMode] = useState<"record" | "upload">("record");

    // Recording state
    const [isRecording, setIsRecording] = useState(false);
    const [recordingTime, setRecordingTime] = useState(0);
    const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
    const [audioUrl, setAudioUrl] = useState<string | null>(null);
    const [audioFormat, setAudioFormat] = useState<string>("webm");
    const [fileName, setFileName] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    // Drag & drop state
    const [isDragging, setIsDragging] = useState(false);

    // Analysis state
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const [analysisResult, setAnalysisResult] = useState<SpeechAnalysisResult | null>(null);
    const [enableProsody, setEnableProsody] = useState(false);

    // Refs
    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const audioChunksRef = useRef<Blob[]>([]);
    const timerRef = useRef<NodeJS.Timeout | null>(null);
    const streamRef = useRef<MediaStream | null>(null);
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            stopAllMedia();
        };
    }, []);

    const stopAllMedia = () => {
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
            mediaRecorderRef.current.stop();
        }
        if (streamRef.current) {
            streamRef.current.getTracks().forEach((track) => track.stop());
            streamRef.current = null;
        }
    };

    const startRecording = async () => {
        try {
            setError(null);
            setAnalysisResult(null);
            setAudioBlob(null);
            setAudioUrl(null);

            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            streamRef.current = stream;

            const mediaRecorder = new MediaRecorder(stream, {
                mimeType: "audio/webm;codecs=opus",
            });

            mediaRecorderRef.current = mediaRecorder;
            audioChunksRef.current = [];

            mediaRecorder.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    audioChunksRef.current.push(event.data);
                }
            };

            mediaRecorder.onstop = () => {
                const blob = new Blob(audioChunksRef.current, { type: "audio/webm" });
                setAudioBlob(blob);
                setAudioUrl(URL.createObjectURL(blob));
                stream.getTracks().forEach((track) => track.stop());
            };

            mediaRecorder.start();
            setIsRecording(true);
            setRecordingTime(0);

            timerRef.current = setInterval(() => {
                setRecordingTime((prev) => {
                    if (prev >= MAX_RECORDING_SECONDS - 1) {
                        stopRecording();
                        return MAX_RECORDING_SECONDS;
                    }
                    return prev + 1;
                });
            }, 1000);
        } catch (err) {
            console.error("Error accessing microphone:", err);
            setError(t("microphoneAccessDenied") || "Could not access microphone");
        }
    };

    const stopRecording = () => {
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
            mediaRecorderRef.current.stop();
        }
        setIsRecording(false);
    };

    const resetRecording = () => {
        setAudioBlob(null);
        setAudioUrl(null);
        setRecordingTime(0);
        setAnalysisResult(null);
        setError(null);
        setFileName(null);
        setAudioFormat("webm");
        audioChunksRef.current = [];
    };

    // File upload handling
    const getFormatFromFile = (file: File): string => {
        const ext = file.name.split(".").pop()?.toLowerCase();
        if (ext === "mp3" || file.type === "audio/mpeg") return "mp3";
        if (ext === "wav") return "wav";
        if (ext === "m4a" || ext === "mp4") return "m4a";
        if (ext === "webm") return "webm";
        return "mp3"; // default
    };

    const handleFileSelect = (file: File) => {
        setError(null);
        setAnalysisResult(null);

        // Validate file type
        if (!ACCEPTED_AUDIO_TYPES.includes(file.type) && !file.name.match(/\.(mp3|wav|m4a|webm)$/i)) {
            setError(t("invalidAudioFormat") || "Invalid audio format. Please use MP3, WAV, M4A or WebM.");
            return;
        }

        // Validate file size
        if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
            setError(t("fileTooLarge") || `File too large. Maximum size: ${MAX_FILE_SIZE_MB}MB`);
            return;
        }

        const format = getFormatFromFile(file);
        setAudioFormat(format);
        setFileName(file.name);
        setAudioBlob(file);
        setAudioUrl(URL.createObjectURL(file));
        setRecordingTime(0); // Will be determined by backend
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);

        const file = e.dataTransfer.files[0];
        if (file) {
            handleFileSelect(file);
        }
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
    };

    const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            handleFileSelect(file);
        }
    };

    const openFileDialog = () => {
        fileInputRef.current?.click();
    };

    const handleAnalyze = async () => {
        if (!audioBlob) return;

        setIsAnalyzing(true);
        setError(null);

        try {
            const token = localStorage.getItem("token");
            if (!token) {
                throw new Error("Not authenticated");
            }

            // Convert blob to base64
            const reader = new FileReader();
            const base64Promise = new Promise<string>((resolve, reject) => {
                reader.onloadend = () => {
                    const base64 = reader.result as string;
                    resolve(base64);
                };
                reader.onerror = reject;
            });
            reader.readAsDataURL(audioBlob);
            const audioData = await base64Promise;

            const result = await analyzeAudio({
                token,
                audioData,
                audioFormat: audioFormat,
                language: "fr",
                enableProsodyAnalysis: enableProsody,
            });

            setAnalysisResult(result);
        } catch (err: any) {
            console.error("Analysis failed:", err);
            setError(err.message || "Analysis failed");
        } finally {
            setIsAnalyzing(false);
        }
    };

    const formatTime = (seconds: number) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
    };

    const getScoreColor = (score: number, max: number = 10) => {
        const normalized = (score / max) * 10;
        if (normalized >= 8) return "#4CAF50";
        if (normalized >= 6) return "#FFC107";
        if (normalized >= 4) return "#FF9800";
        return "#F44336";
    };

    const getSeverityColor = (severity: string) => {
        switch (severity) {
            case "info":
                return "#2196F3";
            case "warn":
                return "#FF9800";
            case "alert":
                return "#F44336";
            default:
                return "#9E9E9E";
        }
    };

    const getSeverityIcon = (severity: string) => {
        switch (severity) {
            case "info":
                return "ℹ️";
            case "warn":
                return "⚠️";
            case "alert":
                return "🚨";
            default:
                return "•";
        }
    };

    const renderProsodyMetricCard = (label: string, value: string | number, status: string, icon: string) => (
        <div className={styles.prosodyMetricCard}>
            <div className={styles.prosodyMetricIcon}>{icon}</div>
            <div className={styles.prosodyMetricContent}>
                <div className={styles.prosodyMetricLabel}>{label}</div>
                <div className={styles.prosodyMetricValue}>{value}</div>
                <div
                    className={styles.prosodyMetricStatus}
                    style={{
                        color:
                            status === "OK" ? "#4CAF50" : status === "LOW" || status === "HIGH" ? "#FF9800" : "#9E9E9E",
                    }}
                >
                    {status}
                </div>
            </div>
        </div>
    );

    const renderScoreCard = (label: string, item: LLMScoreItem, icon: string) => (
        <div className={styles.scoreCard}>
            <div className={styles.scoreIcon}>{icon}</div>
            <div className={styles.scoreContent}>
                <div className={styles.scoreLabel}>{label}</div>
                <div className={styles.scoreValue} style={{ color: getScoreColor(item.score) }}>
                    {item.score}/10
                </div>
                <div className={styles.scoreFeedback}>{item.feedback}</div>
            </div>
        </div>
    );

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <TitleSubtitle
                    title={t("audioAnalysisTest") || "Audio Analysis Test"}
                    subtitle={t("audioAnalysisTestSubtitle") || "Record a sales pitch (max 30s) and get AI feedback"}
                />
            </div>

            <HorizontalSeparator />

            {/* Recording Section */}
            <div className={styles.recordingSection}>
                <h3>{t("audioInput") || "Audio Input"}</h3>

                {/* Mode Tabs */}
                <div className={styles.modeTabs}>
                    <button
                        className={`${styles.modeTab} ${inputMode === "record" ? styles.activeTab : ""}`}
                        onClick={() => {
                            setInputMode("record");
                            resetRecording();
                        }}
                    >
                        🎤 {t("recordAudio") || "Record"}
                    </button>
                    <button
                        className={`${styles.modeTab} ${inputMode === "upload" ? styles.activeTab : ""}`}
                        onClick={() => {
                            setInputMode("upload");
                            resetRecording();
                        }}
                    >
                        📁 {t("uploadAudio") || "Upload File"}
                    </button>
                </div>

                {error && <div className={styles.errorMessage}>{error}</div>}

                <div className={styles.recorderContainer}>
                    {!audioBlob ? (
                        <>
                            {inputMode === "record" ? (
                                /* Microphone Recording Mode */
                                <>
                                    <div className={styles.visualizer}>
                                        <div className={`${styles.micIcon} ${isRecording ? styles.recording : ""}`}>
                                            <svg
                                                width="64"
                                                height="64"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                                stroke="currentColor"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
                                                />
                                            </svg>
                                        </div>
                                    </div>

                                    <div className={styles.timer}>
                                        {formatTime(recordingTime)}
                                        <span className={styles.maxTime}> / {formatTime(MAX_RECORDING_SECONDS)}</span>
                                    </div>

                                    <div className={styles.progressBar}>
                                        <div
                                            className={styles.progressFill}
                                            style={{ width: `${(recordingTime / MAX_RECORDING_SECONDS) * 100}%` }}
                                        />
                                    </div>

                                    <div className={styles.controls}>
                                        {!isRecording ? (
                                            <Button
                                                label={t("startRecording") || "Start Recording"}
                                                onClick={startRecording}
                                                className={styles.startButton}
                                            />
                                        ) : (
                                            <Button
                                                label={t("stopRecording") || "Stop Recording"}
                                                onClick={stopRecording}
                                                className={styles.stopButton}
                                            />
                                        )}
                                    </div>
                                </>
                            ) : (
                                /* File Upload Mode - Dropzone */
                                <>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept=".mp3,.wav,.m4a,.webm,audio/*"
                                        onChange={handleFileInputChange}
                                        style={{ display: "none" }}
                                    />
                                    <div
                                        className={`${styles.dropzone} ${isDragging ? styles.dropzoneDragging : ""}`}
                                        onDrop={handleDrop}
                                        onDragOver={handleDragOver}
                                        onDragLeave={handleDragLeave}
                                        onClick={openFileDialog}
                                    >
                                        <div className={styles.dropzoneIcon}>📁</div>
                                        <p className={styles.dropzoneText}>
                                            {t("dragDropAudio") || "Drag & drop an audio file here"}
                                        </p>
                                        <p className={styles.dropzoneSubtext}>
                                            {t("orClickToSelect") || "or click to select"}
                                        </p>
                                        <p className={styles.dropzoneFormats}>
                                            MP3, WAV, M4A, WebM • Max {MAX_FILE_SIZE_MB}MB
                                        </p>
                                    </div>
                                </>
                            )}
                        </>
                    ) : (
                        <>
                            <div className={styles.playbackContainer}>
                                <div className={styles.successIcon}>✅</div>
                                <p className={styles.successMessage}>
                                    {inputMode === "upload"
                                        ? t("fileLoaded") || "File Loaded"
                                        : t("recordingComplete") || "Recording Complete"}
                                </p>
                                {fileName && <p className={styles.fileName}>📄 {fileName}</p>}
                                <audio controls src={audioUrl || undefined} className={styles.audioPlayer} />
                                {inputMode === "record" && (
                                    <p className={styles.recordingDuration}>
                                        {t("duration") || "Duration"}: {formatTime(recordingTime)}
                                    </p>
                                )}
                            </div>

                            {/* Prosody Toggle */}
                            <div className={styles.toggleContainer}>
                                <label className={styles.toggle}>
                                    <input
                                        type="checkbox"
                                        checked={enableProsody}
                                        onChange={(e) => setEnableProsody(e.target.checked)}
                                    />
                                    <span className={styles.slider}></span>
                                </label>
                                <span className={styles.toggleLabel}>
                                    {t("enableProsodyAnalysis") || "Enable Prosody Analysis (Python Service)"}
                                </span>
                            </div>

                            <div className={styles.playbackControls}>
                                <Button
                                    label={t("recordAgain") || "Record Again"}
                                    onClick={resetRecording}
                                    className={styles.restartButton}
                                />
                                <Button
                                    label={isAnalyzing ? t("analyzing") || "Analyzing..." : t("analyze") || "Analyze"}
                                    onClick={handleAnalyze}
                                    disabled={isAnalyzing || (inputMode === "record" && recordingTime < 3)}
                                    className={styles.analyzeButton}
                                />
                            </div>

                            {inputMode === "record" && recordingTime < 3 && (
                                <p className={styles.warningMessage}>
                                    ⚠️ {t("recordingTooShort") || "Recording too short"} ({recordingTime}s / 3s minimum)
                                </p>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* Analysis Results */}
            {analysisResult && (
                <>
                    <HorizontalSeparator />

                    <div className={styles.resultsSection}>
                        <h3>{t("analysisResults") || "Analysis Results"}</h3>

                        {/* Overall Score */}
                        <div className={styles.overallScore}>
                            <div
                                className={styles.overallScoreCircle}
                                style={{
                                    borderColor: getScoreColor(analysisResult.llm_analysis.overall_score),
                                }}
                            >
                                <span className={styles.overallScoreValue}>
                                    {analysisResult.llm_analysis.overall_score.toFixed(1)}
                                </span>
                                <span className={styles.overallScoreMax}>/10</span>
                            </div>
                            <p className={styles.overallScoreLabel}>{t("overallScore") || "Overall Score"}</p>
                        </div>

                        {/* Summary */}
                        <div className={styles.summaryCard}>
                            <h4>📝 {t("summary") || "Summary"}</h4>
                            <p>{analysisResult.llm_analysis.summary}</p>
                        </div>

                        {/* Score Cards */}
                        <div className={styles.scoreGrid}>
                            {renderScoreCard(
                                t("upsellQuality") || "Upsell Quality",
                                analysisResult.llm_analysis.upsell_quality,
                                "🎯",
                            )}
                            {renderScoreCard(
                                t("politeness") || "Politeness",
                                analysisResult.llm_analysis.politeness,
                                "🤝",
                            )}
                            {renderScoreCard(t("kindness") || "Kindness", analysisResult.llm_analysis.kindness, "💚")}
                            {renderScoreCard(
                                t("customerBenefit") || "Customer Benefit",
                                analysisResult.llm_analysis.customer_benefit,
                                "🎁",
                            )}
                            {renderScoreCard(
                                t("openingQuestion") || "Opening Question",
                                {
                                    ...analysisResult.llm_analysis.opening_question,
                                    feedback: `${analysisResult.llm_analysis.opening_question.present ? "✓ " : "✗ "}${analysisResult.llm_analysis.opening_question.feedback}`,
                                },
                                "❓",
                            )}
                            {renderScoreCard(t("softSell") || "Soft Sell", analysisResult.llm_analysis.soft_sell, "🌟")}
                        </div>

                        {/* Strengths & Improvements */}
                        <div className={styles.feedbackGrid}>
                            <div className={styles.feedbackCard}>
                                <h4>💪 {t("strengths") || "Strengths"}</h4>
                                <ul>
                                    {analysisResult.llm_analysis.top_strengths.map((strength, idx) => (
                                        <li key={idx}>{strength}</li>
                                    ))}
                                </ul>
                            </div>
                            <div className={styles.feedbackCard}>
                                <h4>📈 {t("improvements") || "Areas for Improvement"}</h4>
                                <ul>
                                    {analysisResult.llm_analysis.improvements.map((improvement, idx) => (
                                        <li key={idx}>{improvement}</li>
                                    ))}
                                </ul>
                            </div>
                        </div>

                        {/* Raw LLM JSON (collapsible) */}
                        <details className={styles.rawJsonDetails}>
                            <summary className={styles.rawJsonSummary}>
                                🔧 {t("viewRawLlmJson") || "View Raw LLM JSON"}
                            </summary>
                            <pre className={styles.rawJsonContent}>
                                {JSON.stringify(analysisResult.llm_analysis, null, 2)}
                            </pre>
                        </details>

                        {/* Transcription */}
                        <div className={styles.transcriptCard}>
                            <h4>📜 {t("transcription") || "Transcription"}</h4>
                            <p className={styles.transcriptText}>{analysisResult.transcript.text}</p>
                            <p className={styles.transcriptMeta}>
                                {t("duration") || "Duration"}: {analysisResult.meta.duration_sec.toFixed(1)}s
                            </p>
                        </div>

                        {/* Prosody Results (if enabled) */}
                        {enableProsody && analysisResult.prosody_analysis && (
                            <div className={styles.prosodySection}>
                                <h3>🎵 {t("prosodyAnalysis") || "Prosody Analysis (Python Service)"}</h3>

                                {analysisResult.prosody_analysis.available === false ? (
                                    <div className={styles.prosodyUnavailable}>
                                        <span>⚠️</span>
                                        <p>
                                            {t("prosodyUnavailable") || "Prosody service not available"}:{" "}
                                            {analysisResult.prosody_analysis.error}
                                        </p>
                                    </div>
                                ) : (
                                    <>
                                        {/* Overall Prosody Score */}
                                        {analysisResult.prosody_analysis.overall_score !== undefined && (
                                            <div className={styles.prosodyOverallScore}>
                                                <div
                                                    className={styles.prosodyScoreCircle}
                                                    style={{
                                                        borderColor: getScoreColor(
                                                            analysisResult.prosody_analysis.overall_score,
                                                            100,
                                                        ),
                                                    }}
                                                >
                                                    <span className={styles.prosodyScoreValue}>
                                                        {analysisResult.prosody_analysis.overall_score.toFixed(0)}
                                                    </span>
                                                    <span className={styles.prosodyScoreMax}>/100</span>
                                                </div>
                                                <p className={styles.prosodyScoreLabel}>
                                                    {t("prosodyScore") || "Prosody Score"}
                                                </p>
                                            </div>
                                        )}

                                        {/* Metrics Grid */}
                                        {analysisResult.prosody_analysis.metrics && (
                                            <div className={styles.prosodyMetricsGrid}>
                                                {renderProsodyMetricCard(
                                                    t("speechRate") || "Speech Rate",
                                                    `${analysisResult.prosody_analysis.metrics.wpm} WPM`,
                                                    analysisResult.prosody_analysis.metrics.speech_rate_label || "N/A",
                                                    "🗣️",
                                                )}
                                                {renderProsodyMetricCard(
                                                    t("silenceRatio") || "Silence Ratio",
                                                    `${(analysisResult.prosody_analysis.metrics.silence_ratio * 100).toFixed(0)}%`,
                                                    analysisResult.prosody_analysis.metrics.silence_ratio > 0.3
                                                        ? "HIGH"
                                                        : analysisResult.prosody_analysis.metrics.silence_ratio < 0.1
                                                          ? "LOW"
                                                          : "OK",
                                                    "🔇",
                                                )}
                                                {renderProsodyMetricCard(
                                                    t("pauseCount") || "Pauses",
                                                    `${analysisResult.prosody_analysis.metrics.pause_count} (${analysisResult.prosody_analysis.metrics.pauses_per_min.toFixed(1)}/min)`,
                                                    analysisResult.prosody_analysis.metrics.pauses_per_min > 10
                                                        ? "HIGH"
                                                        : analysisResult.prosody_analysis.metrics.pauses_per_min < 3
                                                          ? "LOW"
                                                          : "OK",
                                                    "⏸️",
                                                )}
                                                {renderProsodyMetricCard(
                                                    t("fillerWords") || "Filler Words",
                                                    `${analysisResult.prosody_analysis.metrics.filler_words?.total || 0} (${(analysisResult.prosody_analysis.metrics.filler_words?.per_min || 0).toFixed(1)}/min)`,
                                                    (analysisResult.prosody_analysis.metrics.filler_words?.per_min ||
                                                        0) > 6
                                                        ? "HIGH"
                                                        : (analysisResult.prosody_analysis.metrics.filler_words
                                                                ?.per_min || 0) > 3
                                                          ? "WARN"
                                                          : "OK",
                                                    "💬",
                                                )}
                                            </div>
                                        )}

                                        {/* Prosody Details */}
                                        {analysisResult.prosody_analysis.metrics?.prosody && (
                                            <div className={styles.prosodyDetailsCard}>
                                                <h4>🎼 {t("voiceCharacteristics") || "Voice Characteristics"}</h4>
                                                <div className={styles.prosodyDetailsGrid}>
                                                    <div className={styles.prosodyDetail}>
                                                        <span className={styles.prosodyDetailLabel}>
                                                            {t("pitchMean") || "Average Pitch"}
                                                        </span>
                                                        <span className={styles.prosodyDetailValue}>
                                                            {analysisResult.prosody_analysis.metrics.prosody.pitch_mean_hz.toFixed(
                                                                0,
                                                            )}{" "}
                                                            Hz
                                                        </span>
                                                    </div>
                                                    <div className={styles.prosodyDetail}>
                                                        <span className={styles.prosodyDetailLabel}>
                                                            {t("pitchRange") || "Pitch Range"}
                                                        </span>
                                                        <span className={styles.prosodyDetailValue}>
                                                            {analysisResult.prosody_analysis.metrics.prosody.pitch_range_hz.toFixed(
                                                                0,
                                                            )}{" "}
                                                            Hz
                                                        </span>
                                                    </div>
                                                    <div className={styles.prosodyDetail}>
                                                        <span className={styles.prosodyDetailLabel}>
                                                            {t("pitchVariability") || "Variability"}
                                                        </span>
                                                        <span
                                                            className={styles.prosodyDetailValue}
                                                            style={{
                                                                color:
                                                                    analysisResult.prosody_analysis.metrics.prosody
                                                                        .pitch_variability === "HIGH"
                                                                        ? "#4CAF50"
                                                                        : analysisResult.prosody_analysis.metrics
                                                                                .prosody.pitch_variability === "MEDIUM"
                                                                          ? "#FFC107"
                                                                          : "#FF9800",
                                                            }}
                                                        >
                                                            {
                                                                analysisResult.prosody_analysis.metrics.prosody
                                                                    .pitch_variability
                                                            }
                                                        </span>
                                                    </div>
                                                    <div className={styles.prosodyDetail}>
                                                        <span className={styles.prosodyDetailLabel}>
                                                            {t("energyLevel") || "Energy Level"}
                                                        </span>
                                                        <span className={styles.prosodyDetailValue}>
                                                            {(
                                                                analysisResult.prosody_analysis.metrics.prosody
                                                                    .energy_mean * 100
                                                            ).toFixed(0)}
                                                            %
                                                        </span>
                                                    </div>
                                                    <div className={styles.prosodyDetail}>
                                                        <span className={styles.prosodyDetailLabel}>
                                                            {t("monotonyScore") || "Monotony"}
                                                        </span>
                                                        <span
                                                            className={styles.prosodyDetailValue}
                                                            style={{
                                                                color:
                                                                    analysisResult.prosody_analysis.metrics.prosody
                                                                        .monotony_score > 0.65
                                                                        ? "#FF9800"
                                                                        : "#4CAF50",
                                                            }}
                                                        >
                                                            {(
                                                                analysisResult.prosody_analysis.metrics.prosody
                                                                    .monotony_score * 100
                                                            ).toFixed(0)}
                                                            %
                                                        </span>
                                                    </div>
                                                    <div className={styles.prosodyDetail}>
                                                        <span className={styles.prosodyDetailLabel}>
                                                            {t("voicingRatio") || "Voicing"}
                                                        </span>
                                                        <span className={styles.prosodyDetailValue}>
                                                            {(
                                                                analysisResult.prosody_analysis.metrics.prosody
                                                                    .voicing_ratio * 100
                                                            ).toFixed(0)}
                                                            %
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {/* Labels/Warnings */}
                                        {analysisResult.prosody_analysis.labels &&
                                            analysisResult.prosody_analysis.labels.length > 0 && (
                                                <div className={styles.prosodyLabelsCard}>
                                                    <h4>📊 {t("detailedFeedback") || "Detailed Feedback"}</h4>
                                                    <div className={styles.prosodyLabelsList}>
                                                        {analysisResult.prosody_analysis.labels.map(
                                                            (label: any, idx: number) => (
                                                                <div
                                                                    key={idx}
                                                                    className={styles.prosodyLabel}
                                                                    style={{
                                                                        borderLeftColor: getSeverityColor(
                                                                            label.severity,
                                                                        ),
                                                                    }}
                                                                >
                                                                    <span className={styles.prosodyLabelIcon}>
                                                                        {getSeverityIcon(label.severity)}
                                                                    </span>
                                                                    <span className={styles.prosodyLabelMessage}>
                                                                        {label.message}
                                                                    </span>
                                                                </div>
                                                            ),
                                                        )}
                                                    </div>
                                                </div>
                                            )}

                                        {/* Tips */}
                                        {analysisResult.prosody_analysis.tips &&
                                            analysisResult.prosody_analysis.tips.length > 0 && (
                                                <div className={styles.prosodyTipsCard}>
                                                    <h4>💡 {t("tips") || "Tips for Improvement"}</h4>
                                                    <ul className={styles.prosodyTipsList}>
                                                        {analysisResult.prosody_analysis.tips.map(
                                                            (tip: string, idx: number) => (
                                                                <li key={idx}>{tip}</li>
                                                            ),
                                                        )}
                                                    </ul>
                                                </div>
                                            )}

                                        {/* Highlights (temporal events) */}
                                        {analysisResult.prosody_analysis.highlights &&
                                            analysisResult.prosody_analysis.highlights.length > 0 && (
                                                <div className={styles.prosodyHighlightsCard}>
                                                    <h4>⏱️ {t("temporalHighlights") || "Timeline Events"}</h4>
                                                    <div className={styles.prosodyHighlightsList}>
                                                        {analysisResult.prosody_analysis.highlights.map(
                                                            (highlight: any, idx: number) => (
                                                                <div key={idx} className={styles.prosodyHighlight}>
                                                                    <span className={styles.prosodyHighlightTime}>
                                                                        {highlight.t.toFixed(1)}s
                                                                    </span>
                                                                    <span className={styles.prosodyHighlightType}>
                                                                        {highlight.type === "long_pause"
                                                                            ? "⏸️"
                                                                            : highlight.type === "speed_up"
                                                                              ? "⚡"
                                                                              : highlight.type === "slow_down"
                                                                                ? "🐢"
                                                                                : "📍"}
                                                                    </span>
                                                                    <span className={styles.prosodyHighlightMessage}>
                                                                        {highlight.message}
                                                                    </span>
                                                                </div>
                                                            ),
                                                        )}
                                                    </div>
                                                </div>
                                            )}

                                        {/* Raw JSON (collapsible) */}
                                        <details className={styles.rawJsonDetails}>
                                            <summary className={styles.rawJsonSummary}>
                                                🔧 {t("viewRawJson") || "View Raw JSON"}
                                            </summary>
                                            <pre className={styles.rawJsonContent}>
                                                {JSON.stringify(
                                                    analysisResult.prosody_analysis?._raw ||
                                                        analysisResult.prosody_analysis,
                                                    null,
                                                    2,
                                                )}
                                            </pre>
                                        </details>
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default AudioAnalysisTest;
